import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

class ScrollAnimation {
  constructor(lenis) {
    this.lenis = lenis
    this.canvas = document.getElementById('apple-animation')
    this.events()
  }

  events() {
    if (!this.canvas) return

    let _this = this

    const MOBILE_BREAKPOINT = 769
    const MOBILE_CANVAS_SIZE = 900
    const DESKTOP_CANVAS_SIZE = 1920

    // Canvas dimensions (sequence asset size)
    const setCanvasDimensions = () => {
      const canvasSize = window.innerWidth < MOBILE_BREAKPOINT ? MOBILE_CANVAS_SIZE : DESKTOP_CANVAS_SIZE
      _this.canvas.width = canvasSize
      _this.canvas.height = canvasSize
    }
    setCanvasDimensions()

    let scrollDistance
    let viewportHeightPx = window.innerHeight || document.documentElement.clientHeight || 0
    let hasQueuedEntranceUnlock = false

    // DOM element cache
    const el = {
      hero: document.getElementById('js-hero'),
      mainContent: document.getElementById('js-main-content'),
      canvasTriggerWrapper: document.getElementById('js-canvas-trigger-wrapper'),
      canvasWrapper: document.getElementById('js-canvas-wrapper'),
      sectionDots: document.getElementById('js-section-dots'),
      bgGradient: document.getElementById('js-bg-gradient'),
      section1: document.getElementById('js-section-1'),
      section2: document.getElementById('js-section-2'),
      section3: document.getElementById('js-section-3'),
      section4: document.getElementById('js-section-4'),
      section5: document.getElementById('js-section-5'),
      shopContainer: document.getElementById('js-shop-items'),
      bgVideo: document.getElementById('js-bg-video'),
      footer: document.getElementById('js-footer'),
      contentEndVideo: document.getElementById('js-content-end-video'),
      contentEndVideoInner: document.getElementById('js-content-end-video-inner')
    }

    const isTouchDevice = () => {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0
    }

    const contentSectionsForScroll = document.querySelectorAll('#js-main-content .content-section')
    const ledeElements = document.querySelectorAll('.js-lede-text')
    const textContentElements = document.querySelectorAll('.js-text-content')
    const quoteElements = document.querySelectorAll('.js-quote')
    const parallaxHeadings = document.querySelectorAll('.js-parallax-heading')

    // Compute scrollable height from content sections and set hero height so scrub distance matches content.
    // During entrance we also reset scroll/lenis; on resize we only recalc + refresh triggers.
    const setScrollDistance = (resetScrollState = false) => {
      viewportHeightPx = window.innerHeight || document.documentElement.clientHeight || 0
      const sectionsHeight = contentSectionsForScroll.length
        ? [...contentSectionsForScroll].reduce((sum, s) => sum + s.getBoundingClientRect().height, 0)
        : viewportHeightPx * 2

      scrollDistance = Math.max(0, sectionsHeight - viewportHeightPx)
      if (el.hero && scrollDistance != null) el.hero.style.height = `${scrollDistance}px`

      if (!resetScrollState) {
        ScrollTrigger.refresh(true)
        return
      }

      // Keep Lenis stopped during entrance; avoid overflow locking to prevent scrollbar-induced layout shift.
      if (_this.lenis) _this.lenis.stop()
      window.scrollTo(0, 0)
      if (_this.lenis) _this.lenis.scrollTo(0, { immediate: true })

      const applyTopOfPageGradientState = () => {
        if (!el.bgGradient) return
        const currentScrollY = window.scrollY || window.pageYOffset || 0
        if (currentScrollY > 1) return
        gsap.set(el.bgGradient, { yPercent: -33, opacity: 1, rotation: 0 })
      }

      const releaseScrollLock = () => {
        window.scrollTo(0, 0)
        if (_this.lenis) {
          _this.lenis.scrollTo(0, { immediate: true })
          _this.lenis.start()
        }
        ScrollTrigger.refresh()
        animation.frame = 0
        render()
        applyTopOfPageGradientState()
        requestAnimationFrame(() => applyTopOfPageGradientState())
      }

      setTimeout(() => {
        if (window.__entranceComplete === true) {
          releaseScrollLock()
          return
        }

        if (!hasQueuedEntranceUnlock) {
          hasQueuedEntranceUnlock = true
          window.addEventListener('entrance:complete', () => {
            hasQueuedEntranceUnlock = false
            releaseScrollLock()
          }, { once: true })
        }
      }, 150)
    }

    setScrollDistance(true)

    const context = _this.canvas.getContext('2d')
    if (!context) return

    // Preload sequence frames (desktop: sequence_2, mobile: sequence_2_mobile below 1024px)
    const desktopImgVersion = '1.4'
    const mobileImgVersion = '1.4'
    const frameCount = 762

    const getFrameSrc = (index, isMobile) => {
      const folder = isMobile ? 'sequence_2_mobile' : 'sequence_2'
      const version = isMobile ? mobileImgVersion : desktopImgVersion
      return `../../assets/${folder}/apple_0${(index + 1).toString()}.webp?v=${version}`
    }

    const imagesRef = { current: [] }
    const animation = { frame: 0 }
    let lastViewportMobile = window.innerWidth < MOBILE_BREAKPOINT
    let hasStartedSequenceLoading = false
    let sequenceLoadToken = 0
    let scrollFramesLoadedCount = 0

    const loadSequence = (isMobile) => {
      const loadToken = ++sequenceLoadToken
      scrollFramesLoadedCount = 0
      if (typeof window !== 'undefined') {
        window.__scrollFramesLoadedCount = 0
        window.__scrollFramesLoaded = false
      }
      const arr = []
      for (let i = 0; i < frameCount; i++) {
        if (loadToken !== sequenceLoadToken) break
        const img = new Image()
        const onLoadOrError = () => {
          scrollFramesLoadedCount += 1
          if (typeof window !== 'undefined') {
            window.__scrollFramesLoadedCount = scrollFramesLoadedCount
            if (scrollFramesLoadedCount >= frameCount - 1) {
              window.__scrollFramesLoaded = true
              window.dispatchEvent(new CustomEvent('scroll-frames:loaded'))
            }
          }
          render()
        }
        img.onload = onLoadOrError
        img.onerror = onLoadOrError
        img.src = getFrameSrc(i, isMobile)
        arr.push(img)
      }
      imagesRef.current = arr
    }

    const startSequenceLoading = () => {
      if (hasStartedSequenceLoading) return
      hasStartedSequenceLoading = true
      loadSequence(lastViewportMobile)
      render()
    }

    if (window.__entranceFramesLoaded === true) {
      startSequenceLoading()
    } else {
      window.addEventListener('entrance:frames-loaded', startSequenceLoading, { once: true })
    }

    // Pin canvas wrapper while main content scrolls over it
    if (el.mainContent && el.canvasTriggerWrapper) {
      gsap.to({}, {
        duration: 0,
        scrollTrigger: {
          trigger: el.mainContent,
          start: 'top top',
          end: 'bottom top',
          pin: el.canvasTriggerWrapper,
          invalidateOnRefresh: true
        }
      })
    }

    // Scrub animation frame 0..frameCount-1 over the hero scroll distance.
    // Three pause points: hold until the corresponding section trigger hits viewport.
    const FIRST_PAUSE_FRAME = 39   // Pause until #js-section-1 bottom hits viewport center
    const SECOND_PAUSE_FRAME = 163 // Pause until #js-section-3 top hits viewport bottom
    const THIRD_PAUSE_FRAME = 251  // Pause until #js-section-3 bottom hits viewport center

    if (el.hero) {
      // Capture scroll progress when each resume trigger fires (used to allocate remaining scroll)
      let progressWhenResume1 = null // When section1 bottom first hits center
      let progressWhenResume2 = null // When section3 top first hits bottom
      let progressWhenResume3 = null // When section3 bottom first hits center
      const scrubProxy = { progress: 0 }

      gsap.to(scrubProxy, {
        progress: 1,
        ease: 'none',
        scrollTrigger: {
          id: 'hero-frame-scrub',
          trigger: el.hero,
          start: 'top top',
          end: () => `+=${scrollDistance}`,
          scrub: true,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const currentScroll = typeof self.scroll === 'function' ? self.scroll() : window.scrollY

            // Resume triggers: section1 bottom crosses center; section3 top crosses viewport bottom; section3 bottom crosses center
            const viewportCenter = viewportHeightPx / 2
            const section1Bottom = el.section1 ? el.section1.getBoundingClientRect().bottom : Infinity
            const section3Top = el.section3 ? el.section3.getBoundingClientRect().top : Infinity
            const section3Bottom = el.section3 ? el.section3.getBoundingClientRect().bottom : Infinity
            const section1BottomAtCenter = section1Bottom <= viewportCenter
            const section3TopAtBottom = section3Top <= viewportHeightPx
            const section3BottomAtCenter = section3Bottom <= viewportCenter

            // Scroll-to-frame mapping: same proportional allocation as frame ranges
            const progressToFrame39 = FIRST_PAUSE_FRAME / (frameCount - 1)
            const fractionTo163 = (SECOND_PAUSE_FRAME - FIRST_PAUSE_FRAME) / (frameCount - 1 - FIRST_PAUSE_FRAME)
            const fractionTo251 = (THIRD_PAUSE_FRAME - SECOND_PAUSE_FRAME) / (frameCount - 1 - SECOND_PAUSE_FRAME)
            let targetFrame

            if (!section1BottomAtCenter) {
              // Phase 1 & 2: scrub 0→39, hold at 39 until section1 bottom reaches center
              progressWhenResume1 = null
              progressWhenResume2 = null
              progressWhenResume3 = null
              targetFrame = Math.min(FIRST_PAUSE_FRAME, Math.round(self.progress / progressToFrame39 * FIRST_PAUSE_FRAME))
              if (el.section2) el.section2.style.zIndex = '2'
            } else if (!section3TopAtBottom) {
              // Phase 3 & 4: scrub 39→163, hold at 163 until section3 top reaches bottom
              progressWhenResume2 = null
              progressWhenResume3 = null
              if (progressWhenResume1 == null) {
                progressWhenResume1 = self.progress
              }
              // Allocate fractionTo163 of remaining scroll (from resume1) for 39→163
              const remaining1 = 1 - progressWhenResume1
              const progressAtFrame163 = progressWhenResume1 + fractionTo163 * remaining1
              if (self.progress < progressAtFrame163) {
                // Still scrubbing toward frame 163
                const segmentLength = progressAtFrame163 - progressWhenResume1
                const progressInPhase3 = segmentLength > 0 ? (self.progress - progressWhenResume1) / segmentLength : 1
                targetFrame = Math.round(FIRST_PAUSE_FRAME + progressInPhase3 * (SECOND_PAUSE_FRAME - FIRST_PAUSE_FRAME))
                if (el.section2) el.section2.style.zIndex = '2'
              } else {
                // Reached 163, holding until section3 top crosses bottom
                targetFrame = SECOND_PAUSE_FRAME
                if (el.section2) el.section2.style.zIndex = '6'
              }
            } else if (!section3BottomAtCenter) {
              // Phase 5 & 6: scrub 163→251, hold at 251 until section3 bottom reaches viewport center
              if (el.section2) el.section2.style.zIndex = '2'
              progressWhenResume3 = null
              if (progressWhenResume2 == null) {
                progressWhenResume2 = self.progress
              }
              const remaining2 = 1 - progressWhenResume2
              const progressAtFrame251 = progressWhenResume2 + fractionTo251 * remaining2
              if (self.progress < progressAtFrame251) {
                const segmentLength = progressAtFrame251 - progressWhenResume2
                const progressInPhase5 = segmentLength > 0 ? (self.progress - progressWhenResume2) / segmentLength : 1
                targetFrame = Math.round(SECOND_PAUSE_FRAME + progressInPhase5 * (THIRD_PAUSE_FRAME - SECOND_PAUSE_FRAME))
              } else {
                // Reached 251, holding until section3 bottom crosses viewport center
                targetFrame = THIRD_PAUSE_FRAME
              }
            } else {
              // Phase 7: section3 bottom at center — scrub 251→end over remaining scroll
              if (el.section2) el.section2.style.zIndex = '2'
              if (progressWhenResume3 == null) {
                progressWhenResume3 = self.progress
              }
              const remaining3 = 1 - progressWhenResume3
              const progressInPhase7 = remaining3 > 0 ? (self.progress - progressWhenResume3) / remaining3 : 1
              targetFrame = Math.round(THIRD_PAUSE_FRAME + progressInPhase7 * (frameCount - 1 - THIRD_PAUSE_FRAME))
            }

            // Skip frame update when scroll has effectively stopped to prevent extra frame glitch
            const scrollStopped = _this.lenis && Math.abs(_this.lenis.velocity) < 0.5
            if (!scrollStopped) {
              animation.frame = Math.min(frameCount - 1, Math.max(0, targetFrame))
            }

            // bgGradient: 0–39 yPercent -33→-50, opacity 1→0; 39–251 stay; 251+ rotate 180deg; opacity→1 when section5 top at viewport bottom
            if (el.bgGradient) {
              if (currentScroll <= 1) {
                gsap.set(el.bgGradient, { yPercent: -33, opacity: 1, rotation: 0 })
                render()
                return
              }

              const frame = animation.frame
              let yPercent, opacity, rotation
              if (frame <= FIRST_PAUSE_FRAME) {
                const progress = frame / FIRST_PAUSE_FRAME
                yPercent = -33 + progress * (-50 - -33)
                opacity = 1 - progress
                rotation = 0
              } else if (frame >= THIRD_PAUSE_FRAME) {
                yPercent = 0
                rotation = 180
              } else {
                yPercent = -50
                rotation = 0
              }
              gsap.set(el.bgGradient, { yPercent, opacity, rotation })
            }

            render()
          }
        }
      })
    }

    // Parallax: move canvas wrapper up as user scrolls past hero
    if (el.hero && el.canvasWrapper) {
      gsap.fromTo(el.canvasWrapper, { y: 0 }, {
        y: -viewportHeightPx,
        ease: 'none',
        scrollTrigger: {
          trigger: el.hero,
          start: 'bottom top',
          end: () => `+=${viewportHeightPx}`,
          scrub: true,
          invalidateOnRefresh: true
        }
      })
    }

    // Lede text: when top of .js-lede-text hits viewport center, reveal nested spans over 150vh
    let vh150 = () => viewportHeightPx * 1.5
    ledeElements.forEach((ledeEl) => {
      const spans = ledeEl.querySelectorAll('span')
      if (spans.length === 0) return
      const isSection5 = !!ledeEl.closest('#js-section-5')
      const minOpacity = 0.2
      gsap.set(ledeEl, { opacity: 0 })
      spans.forEach((s) => gsap.set(s, { opacity: minOpacity }))

      const ledeProxy = { progress: 0 }
      gsap.fromTo(ledeProxy, { progress: 0 }, {
        progress: 1,
        ease: 'none',
        onUpdate: function () {
          const progress = Math.min(1, ledeProxy.progress * 2)
          const n = spans.length
          spans.forEach((span, i) => {
            const spanProgress = Math.max(0, Math.min(1, progress * n - i))
            const opacity = minOpacity + spanProgress * (1 - minOpacity)
            gsap.set(span, { opacity })
          })
        },
        scrollTrigger: {
          trigger: ledeEl,
          start: isSection5 ? 'top center' : 'center center',
          end: () => `+=${vh150()}`,
          scrub: true,
          invalidateOnRefresh: true,
          onEnter: () => gsap.to(ledeEl, { opacity: 1, duration: 0.5 }),
          onLeaveBack: () => {
            gsap.killTweensOf(ledeEl)
            gsap.to(ledeEl, { opacity: 0, duration: 0.5 })
            spans.forEach((s) => gsap.set(s, { opacity: minOpacity }))
          }
        }
      })
    })

    // Text content: when top of .js-text-content hits viewport center, sequentially fade in and up each nested p over scroll distance
    textContentElements.forEach((textEl) => {
      const items = textEl.querySelectorAll('.js-text-content-item')
      if (items.length === 0) return

      gsap.set(items, { opacity: 0, y: 10 })
      gsap.to(items, {
        opacity: 1,
        y: 0,
        ease: 'none',
        stagger: 0.15,
        scrollTrigger: {
          trigger: textEl,
          start: () => (window.innerWidth <= 768 ? 'top top' : 'top center'),
          end: () => `+=${viewportHeightPx * 0.25}`,
          scrub: true,
          invalidateOnRefresh: true
        }
      })
    })

    // Quote: scale and opacity reveal when top of .js-quote hits viewport center → top;
    // scale back down when parent content section bottom hits viewport center
    quoteElements.forEach((quoteEl) => {
      const contentEl = quoteEl.querySelector('.js-quote-content')
      const parentSection = quoteEl.closest('.js-content-section')
      if (!contentEl) return

      const scaleOutDistance = Math.min(200, viewportHeightPx * 0.25)
      gsap.set(contentEl, { scale: 1, opacity: 0 })

      const quoteProxy = { progress: 0 }
      gsap.to(quoteProxy, {
        progress: 1,
        ease: 'none',
        onUpdate: function () {
          const rect = quoteEl.getBoundingClientRect()
          const parentRect = parentSection?.getBoundingClientRect()
          const vh = viewportHeightPx

          // Scale in: 0→1 as quote top goes from center to top of viewport
          const centerY = vh / 2
          const quoteTop = rect.top
          const scaleInProgress = Math.max(0, Math.min(1, 1 - quoteTop / centerY))

          // Scale out: 0→1 as parent bottom approaches and hits viewport center
          // (parent bottom from centerY+scaleOutDistance down to centerY)
          let scaleOutProgress = 0
          if (parentSection && parentRect) {
            const parentBottom = parentRect.bottom
            scaleOutProgress = Math.max(0, Math.min(1, 1 - (parentBottom - centerY) / scaleOutDistance))
          }

          const scale = 1 - 0.15 * scaleOutProgress
          const opacity = scaleInProgress * (1 - scaleOutProgress)
          gsap.set(contentEl, { scale, opacity })
        },
        scrollTrigger: {
          trigger: parentSection || quoteEl,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          invalidateOnRefresh: true
        }
      })
    })

    // Parallax heading from y:300 to y:0 between section top at viewport bottom and heading at viewport center
    parallaxHeadings.forEach((parallaxHeading) => {
      const contentSection = parallaxHeading.closest('.js-content-section')
      if (!contentSection) return

      const endPoint = parallaxHeading.dataset.end

      gsap.set(parallaxHeading, { y: 300, opacity: 1 })
      gsap.to(parallaxHeading, {
        y: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: contentSection,
          start: 'top bottom',
          endTrigger: contentSection,
          end: 'top 100px',
          scrub: true,
          invalidateOnRefresh: true
        }
      })
      // Fade out once heading center hits viewport center; opacity driven by scroll distance
      gsap.to(parallaxHeading, {
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: parallaxHeading,
          start: endPoint ?? 'center 75%',
          end: () => `+=${viewportHeightPx * 0.5}`,
          scrub: true,
          invalidateOnRefresh: true
        }
      })
    })

    // Shop items: start with 50px horizontal stagger (first has no offset); scrub closed as container top moves from viewport bottom to center
    if (el.shopContainer) {
      const shopItems = el.shopContainer.querySelectorAll('.js-shop-item')
      if (shopItems.length) {
        const mm = gsap.matchMedia()
        mm.add('(min-width: 769px)', () => {
          gsap.set(shopItems, (i) => ({ y: i * 100 }))
          shopItems.forEach((item, i) => {
            gsap.fromTo(
              item,
              { y: i * 100 },
              {
                y: 0,
                ease: 'none',
                scrollTrigger: {
                  trigger: el.shopContainer,
                  start: 'top bottom',
                  end: 'top 25%',
                  scrub: true,
                  invalidateOnRefresh: true
                }
              }
            )
          })
        })
        mm.add('(max-width: 768px)', () => {
          gsap.set(shopItems, (i) => ({ y: i * 200 }))
          shopItems.forEach((item, i) => {
            gsap.fromTo(
              item,
              { y: i * 200 },
              {
                y: 0,
                ease: 'none',
                scrollTrigger: {
                  trigger: el.shopContainer,
                  start: 'top center',
                  end: 'bottom bottom',
                  scrub: true,
                  invalidateOnRefresh: true
                }
              }
            )
          })
        })
      }
    }

    // From Plant to Body: fade bg video in/out over 100lvh (below 1025px) / 100dvh (above 1025px); fade in when section 3 top hits viewport top, fade out when section 3 bottom passes viewport bottom
    if (el.section3 && el.bgVideo) {
      const viewportHeightPx = () => {
        if (typeof window === 'undefined') return 0
        const isBelow1025 = window.innerWidth < 1025
        // Below 1025px: lvh (large viewport)
        if (isBelow1025) {
          return Math.max(window.innerHeight, window.visualViewport?.height || 0) || document.documentElement.clientHeight || 0
        }
        // Above 1025px: dvh (dynamic viewport)
        return window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 0
      }
      gsap.set(el.bgVideo, { opacity: 0, scale: 1 })
      let fadeInProgress = 0
      let fadeOutProgress = 0
      const updateBgVideoOpacity = () => {
        const visible = fadeInProgress * (1 - fadeOutProgress)
        gsap.set(el.bgVideo, { opacity: visible, scale: 1 + 0.2 * visible })
      }
      const fadeInScrubProxy = { progress: 0 }
      gsap.to(fadeInScrubProxy, {
        progress: 1,
        ease: 'none',
        onUpdate: () => {
          fadeInProgress = fadeInScrubProxy.progress
          updateBgVideoOpacity()
        },
        scrollTrigger: {
          trigger: el.section3,
          start: 'top top',
          end: () => `+=${viewportHeightPx()}`,
          scrub: true,
          // invalidateOnRefresh: true
        }
      })
      const fadeOutScrubProxy = { progress: 0 }
      gsap.to(fadeOutScrubProxy, {
        progress: 1,
        ease: 'none',
        onUpdate: () => {
          fadeOutProgress = fadeOutScrubProxy.progress
          updateBgVideoOpacity()
        },
        scrollTrigger: {
          trigger: el.section3,
          start: 'bottom bottom',
          end: () => `+=${viewportHeightPx() / 2}`,
          scrub: true,
          // invalidateOnRefresh: true
        }
      })
    }

    // Fade out section dots when bottom of final content section hits bottom of viewport
    if (el.sectionDots) {
      const finalSection = document.getElementById('js-content-end')

      if (finalSection) {
        gsap.to(el.sectionDots, {
          opacity: 0,
          y: 10,
          duration: 0.35,
          scrollTrigger: {
            trigger: finalSection,
            start: 'top bottom',
            invalidateOnRefresh: true,
            toggleActions: 'play reverse play reverse'
          }
        })
      }
    }

    // Section 4: ScrollTrigger timeline for .js-fade-heading and .js-fade-text
    // When top of .js-fade-text hits viewport center: fade out .js-fade-heading over 0.5vh
    // When top of .js-fade-text hits viewport top: fade in each p like .js-text-content
    if (el.section4) {
      const fadeHeading = el.section4.querySelector('.js-fade-heading')
      const fadeText = el.section4.querySelector('.js-fade-text')
      if (fadeHeading && fadeText) {
        const items = fadeText.querySelectorAll('.js-fade-text-item')
        const stickyDiv = fadeText.querySelector(':scope > div')
        gsap.set(items, { opacity: 0, y: 10 })

        // Fade out heading: starts when fade-text top hits center, over viewportHeightPx * 0.5 scroll
        gsap.to(fadeHeading, {
          opacity: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: fadeText,
            start: 'top top',
            end: () => `+=${viewportHeightPx * 0.5}`,
            scrub: true,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              if (!el.section3) return
              if (self.progress >= 1) {
                el.section3.style.zIndex = '1'
              } else {
                el.section3.style.removeProperty('z-index')
              }
            }
          }
        })

        // Fade in paragraphs when nested div becomes sticky (top: 150px in CSS), over scroll distance like fade heading
        if (stickyDiv) {
          gsap.to(items, {
            opacity: 1,
            y: 0,
            ease: 'none',
            stagger: 0.15,
            scrollTrigger: {
              trigger: stickyDiv,
              start: 'top 150px',
              end: () => `+=${viewportHeightPx * 0.25}`,
              scrub: true,
              invalidateOnRefresh: true
            }
          })
        }
        // Fade out paragraphs when .js-fade-text top hits viewport bottom
        gsap.fromTo(items, { opacity: 1, y: 0 }, {
          opacity: 0,
          y: 10,
          ease: 'none',
          stagger: { each: 0.15, from: 'end' },
          scrollTrigger: {
            trigger: fadeText,
            start: 'top bottom',
            end: () => `+=${viewportHeightPx * 0.25}`,
            scrub: true,
            invalidateOnRefresh: true
          }
        })
      }
    }

    // When section5 top hits viewport bottom, animate bgGradient toward yPercent -50.
    // Use a dedicated trigger callback (instead of a tween on the element) so refresh/top-of-page
    // doesn't force a pre-start transform state during entrance→scroll handoff.
    if (el.bgGradient && el.section5) {
      ScrollTrigger.create({
        trigger: el.section5,
        start: 'top bottom',
        end: () => `+=${viewportHeightPx}`,
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          // Ignore pre-start (progress 0) so other gradient logic owns initial state.
          if (self.progress <= 0) return
          gsap.set(el.bgGradient, {
            yPercent: -50 * self.progress,
            opacity: 1
          })
        }
      })
    }

    // Fade in footer as its top hits the viewport top
    if (el.footer && el.contentEndVideoInner) {
      gsap.set(el.footer, { opacity: 0, pointerEvents: 'none' })
      gsap.fromTo(el.footer,
        { opacity: 0 },
        {
          opacity: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: el.contentEndVideoInner,
            start: 'bottom center',
            end: 'max',
            scrub: true,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              gsap.set(el.footer, { pointerEvents: self.progress > 0.01 ? 'auto' : 'none' })
            },
            onRefresh: (self) => {
              gsap.set(el.footer, { pointerEvents: self.progress > 0.01 ? 'auto' : 'none' })
            }
          }
        }
      )
    }

    // Content-end video: parallax on .js-content-end-video-img while #js-content-end-video is in viewport
    const contentEndImg = el.contentEndVideo?.querySelector('.js-content-end-video-img')
    if (el.contentEndVideo && contentEndImg) {
      gsap.fromTo(contentEndImg,
        { yPercent: -12 },
        {
          yPercent: 12,
          ease: 'none',
          scrollTrigger: {
            trigger: el.contentEndVideo,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
            invalidateOnRefresh: true
          }
        }
      )
    }

    render()

    const applyScrollResetLikeResize = () => {
      setCanvasDimensions()
      render()
      const nowMobile = window.innerWidth < MOBILE_BREAKPOINT
      if (hasStartedSequenceLoading && nowMobile !== lastViewportMobile) {
        lastViewportMobile = nowMobile
        loadSequence(nowMobile)
        render()
      } else {
        lastViewportMobile = nowMobile
      }
      setScrollDistance(false)
      window.scrollTo(0, 0)
      if (_this.lenis) _this.lenis.scrollTo(0, { immediate: true })
      animation.frame = 0
      render()
      if (el.bgGradient) {
        gsap.set(el.bgGradient, { yPercent: -33, opacity: 1, rotation: 0 })
      }
    }

    window.addEventListener('resize', applyScrollResetLikeResize)

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return
      if (window.__entranceComplete !== true) return
      applyScrollResetLikeResize()
    })

    // Draw current frame to canvas; re-run when image loads if not yet ready
    function render() {
      const frame = animation.frame
      const img = imagesRef.current[frame]
      if (!context || !_this.canvas || !img) return
      img.onload = render
      context.clearRect(0, 0, _this.canvas.width, _this.canvas.height)
      context.drawImage(img, 0, 0)
    }
  }
}

export default ScrollAnimation